/**
 * The one i18n runtime every console app mounts.
 *
 * # `t(key, defaultValue)`, and why the English stays in the code
 *
 * The English literal is not moved out of the component, it is moved out of the
 * component AND left there:
 *
 *   <button>{t('common.cancel', 'Cancel')}</button>
 *
 * The catalogue value wins when there is one; the literal is what renders when
 * there is not. That buys three things worth more than the duplication costs:
 *
 *   - A missing or misspelt key renders English, never `common.cancel`. There is
 *     no state of this system where a user is shown a key.
 *   - The diff of an extraction PR is readable. A reviewer sees the copy it is
 *     changing, so a bad key or a wrong-string swap is visible in review rather
 *     than at runtime in a language nobody on the team reads.
 *   - `catalogues/en/` can be regenerated from source at any time and compared,
 *     which is what the coverage guard does.
 *
 * It is the same signature `apps/marketing/app/components/UiCopyProvider.tsx`
 * uses for storefront copy, deliberately, so the two surfaces read alike.
 *
 * # Why i18next and not fifty lines of our own
 *
 * `<Trans>` and ICU. 484 in-scope copy sites are JSX that mixes text with nested
 * elements, and 82 more do their pluralisation by hand with `n !== 1 ? 's' : ''`.
 * Both have exactly one correct answer and neither is worth writing twice.
 * Lingui was the runner-up (nicer compile-time story, more Vite plugin friction);
 * Paraglide has no `<Trans>` equivalent at all.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import ICU from 'i18next-icu';
import {
  I18nextProvider,
  Trans,
  initReactI18next,
  useTranslation,
} from 'react-i18next';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { RUNTIME_NAMESPACE, SUPPORTED_LOCALES, loadCatalogue } from './catalogues';
import { readDeviceLocale, writeDeviceLocale } from './device-choice';
import { createFormatters, type Formatters } from './formatters';
import {
  englishTranslator,
  formatDefault,
  isMessage,
  type Message,
  type Translatable,
  type Translator,
} from './message';

export type { Translator };
import { DEFAULT_LOCALE, browserLocales, resolveLocale } from './resolve';

export { Trans };

/**
 * A namespaced key and the English that renders when the catalogue lacks it.
 *
 * The second form takes a {@link Message}, which is the same pair carried as a
 * value so that copy declared at module scope can be resolved here at render.
 */
/** `{ en: { common: { cancel: 'Cancel' } } }` */
export type Catalogues = Record<string, Record<string, Record<string, string>>>;

interface I18nValue {
  locale: string;
  formatters: Formatters;
  setPreferred: (locale: string | null) => void;
  /** Locales this bundle actually has a catalogue for. */
  supported: readonly string[];
  /** This device's chosen language, for someone with no account to store one on. */
  deviceLocale: string | null;
  setDeviceLocale: (locale: string | null) => void;
}

const I18nContext = createContext<I18nValue | null>(null);

export interface FoirI18nProviderProps {
  /**
   * Catalogues to start with, keyed by locale then namespace.
   *
   * Apps pass nothing: the provider resolves the language and loads that one
   * catalogue itself, so nobody downloads a language they are not being served.
   * Supply it only to hand the provider copy it should not have to fetch — a
   * test with a fixture, or a caller that already awaited
   * {@link loadCatalogue}.
   */
  catalogues?: Catalogues;
  /**
   * Locales the switcher may offer.
   *
   * Defaults to every catalogue compiled into the build, which is what an app
   * wants. When `catalogues` IS supplied it defaults to that object's keys
   * instead, so a fixture is self-contained: a test asserting French does not
   * quietly depend on French existing on disk. Pass it explicitly to preload
   * one locale and still offer the rest.
   */
  supported?: readonly string[];
  /**
   * A starting language, for a caller that knows one before the session does.
   * Most apps pass nothing and let `useSyncPreferredLocale` supply the signed-in
   * user's `preferred_locale` once the session resolves.
   */
  preferred?: string | null;
  children?: ReactNode;
}

/**
 * Render the in-code English when i18next is not there to do it.
 *
 * `useT` deliberately works outside a provider, so a component under test or in
 * isolation renders its English rather than throwing. Returning the raw default
 * was not enough once messages carried parameters: `t(k, 'Filter {name}', {name})`
 * rendered the literal `Filter {name}`, placeholder and all. `@foir/ui`'s own
 * tests caught it, and a component mounted outside the provider in production
 * would have shown the same thing.
 *
 * `intl-messageformat` is already a dependency (i18next-icu is built on it), so
 * the fallback formats exactly as the real path does, plurals included.
 */


function buildInstance(locale: string, catalogues: Catalogues): I18nInstance {
  const instance = i18next.createInstance();
  void instance
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      resources: catalogues,
      // Keys are `namespace.key`, addressed as one dotted string, and every
      // catalogue file is merged into ONE i18next namespace by
      // `loadCatalogue()`. Both separators are off so i18next treats the whole
      // key as a literal lookup rather than splitting it: `keySeparator` would
      // make `common.cancel` a nested object path, `nsSeparator` would make it
      // a namespace reference. Neither is what the key means.
      ns: [RUNTIME_NAMESPACE],
      defaultNS: RUNTIME_NAMESPACE,
      nsSeparator: false,
      keySeparator: false,
      interpolation: { escapeValue: false },
      // A catalogue arrives AFTER init now: it is a per-locale chunk, so the
      // instance is built first and the bundle added when it lands. Without
      // this, `addResourceBundle` updates the store and nothing re-renders,
      // because react-i18next binds to no store event by default.
      react: { bindI18nStore: 'added' },
      // A key with no catalogue entry must render its in-code English, which is
      // what `t()` passes as `defaultValue`. Returning the key instead is the
      // one failure mode this design exists to prevent.
      parseMissingKeyHandler: (_key, defaultValue) => defaultValue ?? '',
      returnNull: false,
    });
  return instance;
}

/**
 * The translator the mounted provider is currently using.
 *
 * For code that has to resolve copy but cannot be a component: the query
 * client's global mutation-error toast is created once, outside the tree, and
 * fires long after. A getter rather than a value because the language changes —
 * reading it at call time gets the current one, holding a reference to it does
 * not.
 *
 * Falls back to the in-code English before a provider mounts, which is the
 * right answer for that window rather than a crash.
 */
let activeTranslator: Translator | null = null;

export function getTranslator(): Translator {
  return activeTranslator ?? englishTranslator;
}

/**
 * A translator for code that runs BEFORE React does.
 *
 * A boot check that finds an unbound domain paints a plain-DOM screen and
 * returns; there is no tree to hang a provider on, and its two sentences are
 * the only thing a user on that address ever sees. This resolves the locale
 * by exactly the rule the provider uses — account choice does not exist yet, so
 * device choice then browser — and hands back the same {@link Translator}.
 *
 * Not a substitute for `useT` anywhere a component could call it: this instance
 * is built once and never follows a later language change.
 */
export function createStandaloneTranslator(
  catalogues: Catalogues,
  supported: readonly string[] = Object.keys(catalogues),
): Translator {
  const locale = resolveLocale({
    preferred: readDeviceLocale(),
    browser: browserLocales(),
    supported,
  });
  const instance = buildInstance(locale, catalogues);
  return ((keyOrMessage: Translatable, second?: unknown, third?: unknown) => {
    const [key, defaultValue, params] = isMessage(keyOrMessage)
      ? [keyOrMessage.key, keyOrMessage.defaultValue, second as Record<string, unknown>]
      : [keyOrMessage, second as string, third as Record<string, unknown>];
    if (defaultValue === undefined) return key;
    return instance.t(key, { defaultValue, ...params }) as string;
  }) as Translator;
}

/**
 * {@link createStandaloneTranslator}, with the catalogue fetched for you.
 *
 * The catalogues are per-locale chunks now, so a caller running before React
 * has no way to hand this function copy it already holds. This resolves the
 * language against every locale in the build, loads that one, and builds the
 * instance from it.
 *
 * Await it where the boot path already has an async boundary. English needs no
 * chunk, so an English reader pays nothing for the await.
 */
export async function loadStandaloneTranslator(): Promise<Translator> {
  const locale = resolveLocale({
    preferred: readDeviceLocale(),
    browser: browserLocales(),
    supported: SUPPORTED_LOCALES,
  });
  return createStandaloneTranslator(await loadCatalogue(locale), [locale]);
}

/**
 * Wraps an app's root. Resolves the locale once, builds the i18next instance and
 * the formatter set for it, and stamps `<html lang>` so assistive technology and
 * the browser's own hyphenation agree with what is on screen.
 */
export function FoirI18nProvider({
  catalogues: seeded,
  supported = seeded ? Object.keys(seeded) : SUPPORTED_LOCALES,
  preferred = null,
  children,
}: FoirI18nProviderProps) {
  // The signed-in user's choice arrives AFTER the first render: the session is
  // fetched, and this provider sits outside the session provider so that the
  // session provider's own copy is translated too. So the preference is state
  // rather than a prop read once, and `useSyncPreferredLocale` pushes it up
  // from inside the session once it lands.
  const [sessionPreferred, setSessionPreferred] = useState<string | null>(null);
  const [deviceLocale, setDeviceLocaleState] = useState<string | null>(readDeviceLocale);

  // This device's choice first, then the account setting, then the browser. See
  // `device-choice.ts` for why that order and not another.
  const effective = deviceLocale ?? sessionPreferred ?? preferred;

  const locale = useMemo(
    () =>
      resolveLocale({
        preferred: effective,
        browser: browserLocales(),
        supported,
      }),
    [effective, supported],
  );

  const setDeviceLocale = useCallback((next: string | null) => {
    writeDeviceLocale(next);
    setDeviceLocaleState(next);
  }, []);

  // Catalogues accumulate across language switches, so a language already seen
  // is instant and no chunk is requested twice. A ref rather than state because
  // it is a cache, not something a render reads reactively: the re-render on
  // arrival comes from i18next's own `added` event, not from this.
  const loaded = useRef<Catalogues>(seeded ?? {});

  // ONE instance per locale, seeded with whatever has been fetched by now.
  //
  // Rebuilding it when a catalogue arrives does not work: `init()` is async, so
  // a fresh instance reports `ready === false`, and `useT` correctly falls back
  // to the in-code English for that window. The copy would never appear. So the
  // instance is stable and the bundle is added to it instead.
  const instance = useMemo(() => buildInstance(locale, loaded.current), [locale]);

  // A locale that has not arrived yet is not an error state and gets no spinner.
  // Every string carries its in-code English, so the tree renders readable
  // English for the one chunk-load and swaps when it lands. Holding the tree
  // back instead would put a fetch in front of first paint on `apps/auth`, the
  // pre-auth login screen, and would turn a failed chunk into a blank page
  // rather than an English one.
  useEffect(() => {
    if (loaded.current[locale]) return;
    let live = true;
    void loadCatalogue(locale).then((next) => {
      // Nothing comes back for English and for a locale with no files. Both
      // render from the defaults, so there is nothing to add.
      const bundle = next[locale]?.[RUNTIME_NAMESPACE];
      if (!live || !bundle) return;
      loaded.current = { ...loaded.current, ...next };
      // `deep` and `overwrite` both true: the seeded catalogue may already hold
      // some of these keys, and the fetched file is the newer answer.
      instance.addResourceBundle(locale, RUNTIME_NAMESPACE, bundle, true, true);
    });
    return () => {
      live = false;
    };
  }, [locale, instance]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      formatters: createFormatters(locale),
      setPreferred: setSessionPreferred,
      supported,
      deviceLocale,
      setDeviceLocale,
    }),
    [locale, supported, deviceLocale, setDeviceLocale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Published for `getTranslator`, so code outside the tree resolves through the
  // SAME instance this render is using. Set during render rather than in an
  // effect: a mutation can fail before effects have run.
  activeTranslator = useMemo(
    () =>
      ((keyOrMessage: Translatable, second?: unknown, third?: unknown) => {
        const [key, defaultValue, params] = isMessage(keyOrMessage)
          ? [keyOrMessage.key, keyOrMessage.defaultValue, second as Record<string, unknown>]
          : [keyOrMessage, second as string, third as Record<string, unknown>];
        if (defaultValue === undefined) return key;
        return instance.t(key, { defaultValue, ...params }) as string;
      }) as Translator,
    [instance],
  );

  return (
    <I18nextProvider i18n={instance}>
      <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    </I18nextProvider>
  );
}

/**
 * Feed the signed-in user's stored language into the resolver.
 *
 * Call it from inside whatever provider knows who is signed in, passing
 * `user?.preferredLocale`. It is a hook rather than a prop on
 * `FoirI18nProvider` because that provider deliberately sits OUTSIDE the session
 * provider, so that the sign-in and session-error screens are translated too;
 * the value therefore has to travel up rather than down.
 *
 * `@foir/i18n` stays free of any dependency on `@foir/auth` this way, which
 * matters because `@foir/auth` renders copy and will import this package.
 *
 * Passing `undefined` (session still loading) or `null` (never chose) both leave
 * the browser's own languages in charge, which is the correct answer for each.
 */
export function useSyncPreferredLocale(preferred: string | null | undefined): void {
  const ctx = useContext(I18nContext);
  const setPreferred = ctx?.setPreferred;
  useEffect(() => {
    setPreferred?.(preferred ?? null);
  }, [preferred, setPreferred]);
}

/**
 * The translator.
 *
 * Outside a provider it returns the in-code default for every key, so a
 * component under test, or in Storybook, or mounted before the provider exists,
 * renders its English rather than throwing or showing a key.
 */
/**
 * A bare instance for components rendered with no provider above them.
 *
 * `useT` already answers that case itself — it formats the in-code English —
 * but react-i18next does not know that, and warns `NO_I18NEXT_INSTANCE` on
 * every such render. A component test mounts a component, not an app, so that
 * is every component test in the repo: noise in the log, and a per-render cost
 * inside a `waitFor` loop on a runner that is already starved.
 *
 * Built lazily and never used for a lookup: the branch below returns before it
 * is asked anything.
 */
let orphan: I18nInstance | null = null;
function orphanInstance(): I18nInstance {
  orphan ??= i18next.createInstance();
  if (!orphan.isInitialized) {
    void orphan.init({
      lng: DEFAULT_LOCALE,
      resources: {},
      ns: [RUNTIME_NAMESPACE],
      defaultNS: RUNTIME_NAMESPACE,
      nsSeparator: false,
      keySeparator: false,
      parseMissingKeyHandler: (_key, defaultValue) => defaultValue ?? '',
      returnNull: false,
    });
  }
  return orphan;
}

export function useT(): Translator {
  const inProvider = useContext(I18nContext) !== null;
  const { t, ready } = useTranslation(undefined, {
    useSuspense: false,
    // Only outside a provider; inside one, `undefined` lets react-i18next take
    // the instance from its own context, which is the one holding the
    // catalogues.
    i18n: inProvider ? undefined : orphanInstance(),
  });
  return useMemo<Translator>(() => {
    const resolve = (
      keyOrMessage: string | Message,
      defaultOrParams?: string | Record<string, unknown>,
      maybeParams?: Record<string, unknown>,
    ): string => {
      // A bare string with no default is already-final text (a user's field
      // name, say). There is nothing to look up and nothing to translate.
      if (typeof keyOrMessage === 'string' && defaultOrParams === undefined) {
        return keyOrMessage;
      }
      const key = isMessage(keyOrMessage) ? keyOrMessage.key : keyOrMessage;
      const defaultValue = isMessage(keyOrMessage)
        ? keyOrMessage.defaultValue
        : (defaultOrParams as string);
      const params = isMessage(keyOrMessage)
        ? (defaultOrParams as Record<string, unknown> | undefined)
        : maybeParams;
      if (!inProvider || !ready) return formatDefault(defaultValue, params);
      return t(key, { defaultValue, ...params }) as string;
    };
    return resolve as Translator;
  }, [t, ready, inProvider]);
}

/**
 * Locale-bound date, number and money formatting.
 *
 * Outside a provider it formats as {@link DEFAULT_LOCALE}, for the same reason
 * {@link useT} falls back to English: a component that renders should render.
 */
export function useFormatters(): Formatters {
  const ctx = useContext(I18nContext);
  const fallback = useMemo(() => createFormatters(DEFAULT_LOCALE), []);
  return ctx?.formatters ?? fallback;
}

/** The resolved locale, for the rare `Intl` call this package does not wrap. */
export function useLocale(): string {
  return useContext(I18nContext)?.locale ?? DEFAULT_LOCALE;
}

/**
 * The language control's state: what is on offer, what is chosen, how to change it.
 *
 * `supported` is every locale compiled into the build, not every locale
 * downloaded — the list is known from the catalogue filenames, so the control
 * can offer a language before that language's copy has been fetched. The
 * switcher renders nothing below two entries: a select with one option is a
 * worse answer than no select.
 */
export function useLocaleChoice(): {
  locale: string;
  supported: readonly string[];
  deviceLocale: string | null;
  setDeviceLocale: (locale: string | null) => void;
} {
  const ctx = useContext(I18nContext);
  return {
    locale: ctx?.locale ?? DEFAULT_LOCALE,
    supported: ctx?.supported ?? [DEFAULT_LOCALE],
    deviceLocale: ctx?.deviceLocale ?? null,
    setDeviceLocale: ctx?.setDeviceLocale ?? (() => {}),
  };
}
